import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import axios from 'axios';
import ProductForm from '../components/ProductForm';
import EditProductForm from '../components/EditProductForm';
import '../styles/ProductForm.css';
import '../styles/Products.css';

const Products = () => {
  const { user } = useAuth();
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [successMessage, setSuccessMessage] = useState('');
  const [showForm, setShowForm] = useState(false);
  const [refreshData, setRefreshData] = useState(false);
  const [isUpdating, setIsUpdating] = useState(false);
  const [editingProduct, setEditingProduct] = useState(null);
  const [showEditForm, setShowEditForm] = useState(false);
  const [searchTerm, setSearchTerm] = useState(''); // Nuevo estado para el término de búsqueda
  const [filteredProducts, setFilteredProducts] = useState([]); // Nuevo estado para los productos filtrados
  const navigate = useNavigate();

  useEffect(() => {
    const fetchProducts = async () => {
      try {
        setLoading(true);
        const token = localStorage.getItem('token');
        
        if (!token) {
          navigate('/login');
          return;
        }
        
        const response = await axios.get('/api/products', {
          headers: {
            Authorization: `Bearer ${token}`
          }
        });
        
        setProducts(response.data);
      } catch (error) {
        console.error('Error fetching products:', error);
        setError('No se pudieron cargar los productos. Por favor, inténtelo de nuevo.');
        
        // If unauthorized, redirect to login
        if (error.response && error.response.status === 401) {
          navigate('/login');
        }
      } finally {
        setLoading(false);
      }
    };

    fetchProducts();
  }, [navigate, refreshData]);

  // useEffect para filtrar productos cuando searchTerm o products cambian
  useEffect(() => {
    if (searchTerm === '') {
      setFilteredProducts(products);
    } else {
      const lowercasedFilter = searchTerm.toLowerCase();
      const filtered = products.filter(product => {
        const codeMatch = product.prod_code && product.prod_code.toLowerCase().includes(lowercasedFilter);
        const nameMatch = product.prod_name && product.prod_name.toLowerCase().includes(lowercasedFilter);
        return codeMatch || nameMatch;
      });
      setFilteredProducts(filtered);
    }
  }, [searchTerm, products]);

  const handleProductAdded = () => {
    // Refresh the products list
    setRefreshData(!refreshData);
    // Hide the form
    setShowForm(false);
    // Show success message
    setSuccessMessage('Producto agregado exitosamente');
    
    // Hide message after 3 seconds
    setTimeout(() => {
      setSuccessMessage('');
    }, 3000);
  };

  const handleEditClick = (product) => {
    setEditingProduct(product);
    setShowEditForm(true);
    setShowForm(false); // Cerrar el formulario de agregar si está abierto
  };

  const handleProductUpdated = () => {
    // Refresh the products list
    setRefreshData(!refreshData);
    // Hide the edit form
    setShowEditForm(false);
    setEditingProduct(null);
    // Show success message
    setSuccessMessage('Producto actualizado exitosamente');
    
    // Hide message after 3 seconds
    setTimeout(() => {
      setSuccessMessage('');
    }, 3000);
  };

  const handleToggleStatus = async (productId, currentStatus) => {
    try {
      setIsUpdating(true);
      const token = localStorage.getItem('token');
      
      if (!token) {
        navigate('/login');
        return;
      }
      
      // Obtener el rol del usuario para determinar el endpoint correcto
      const userRole = user?.role;
      
      console.log('DEBUG: User role detected:', userRole);
      
      // Determinar el endpoint según el rol del usuario
      let endpoint;
      if (userRole === 'manager') {
        // Supervisores usan el endpoint específico
        endpoint = `/api/supervisors/products/${productId}/status`;
        console.log('DEBUG: Using supervisor endpoint:', endpoint);
      } else {
        // Admins y otros roles usan el endpoint original
        endpoint = `/api/products/${productId}/status`;
        console.log('DEBUG: Using admin endpoint:', endpoint);
      }
      
      // Actualizar el estado del producto (invertir el estado actual)
      await axios.put(endpoint, 
        { prod_alive: !currentStatus },
        {
          headers: {
            Authorization: `Bearer ${token}`
          }
        }
      );
      
      // Actualizar la lista de productos
      // Esto también activará el useEffect para filtrar de nuevo si es necesario
      setRefreshData(!refreshData);
      
    } catch (error) {
      console.error('Error al actualizar el estado del producto:', error);
      setError('Error al actualizar el estado del producto. Por favor, inténtelo de nuevo.');
      
      // Si no está autorizado, redirigir al login
      if (error.response && error.response.status === 401) {
        navigate('/login');
      }
    } finally {
      setIsUpdating(false);
    }
  };

  if (loading && products.length === 0) {
    return <div className="loading">Cargando productos...</div>;
  }

  return (
    <div className="dashboard-container">
      <header className="dashboard-header">
        <h1>Gestión de Productos</h1>
        <button onClick={() => navigate('/dashboard')} className="back-btn">
          Volver al Dashboard
        </button>
      </header>

      <div className="products-section">
        <div className="products-section-header">
          <h2>Productos Registrados</h2>
          <button 
            className="add-product-btn"
            onClick={() => setShowForm(true)}
          >
            <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              <line x1="12" y1="5" x2="12" y2="19"></line>
              <line x1="5" y1="12" x2="19" y2="12"></line>
            </svg>
            Agregar Producto
          </button>
        </div>

        {error && (
          <div className="error-message">
            {error}
          </div>
        )}

        {successMessage && (
          <div className="success-message">
            {successMessage}
          </div>
        )}

        {showForm ? (
          <ProductForm 
            onProductAdded={handleProductAdded} 
            onCancel={() => setShowForm(false)} 
          />
        ) : null}

        {showEditForm && editingProduct ? (
          <EditProductForm 
            product={editingProduct}
            onProductUpdated={handleProductUpdated}
            onCancel={() => {
              setShowEditForm(false);
              setEditingProduct(null);
            }}
          />
        ) : null}

        {/* Input de búsqueda */}
        <div className="search-container" style={{ marginBottom: '20px', marginTop: '20px' }}>
          <input
            type="text"
            placeholder="Buscar por código o nombre..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="search-input"
            style={{ width: '100%', padding: '10px', borderRadius: '4px', border: '1px solid #ccc' }}
          />
        </div>

        <div className="products-list">
          {loading && filteredProducts.length === 0 && searchTerm === '' ? (
            <p className="loading-message">Cargando productos...</p>
          ) : !loading && products.length === 0 ? (
            <p className="no-products">No hay productos registrados. 
              <button onClick={() => setShowForm(true)}>Agregar Producto</button>
            </p>
          ) : filteredProducts.length === 0 && searchTerm !== '' ? (
            <p className="no-products">No se encontraron productos que coincidan con \"{searchTerm}\".</p>
          ) : filteredProducts.length === 0 && products.length > 0 && searchTerm === '' ? (
             // Esto puede pasar brevemente mientras products carga y filteredProducts aún no se actualiza
             // O si todos los productos son filtrados por alguna otra razón no implementada aquí
            <p className="no-products">No hay productos para mostrar.</p>
          ) : (
            <table className="products-table">
              <thead>
                <tr>
                  <th>Código</th>
                  <th>Nombre</th>
                  <th>Descripción</th>
                  <th>Precio</th>
                  <th>Stock</th>
                  <th>Estado</th>
                  <th>Acciones</th>
                </tr>
              </thead>
              <tbody>
                {filteredProducts.map(product => (
                  <tr key={product.id}>
                    <td>{product.prod_code || '-'}</td>
                    <td>{product.prod_name}</td>
                    <td>{product.prod_description || 'Sin descripción'}</td>
                    <td>${parseFloat(product.prod_price).toFixed(2)}</td>
                    <td>{product.prod_stock || 0}</td>
                    <td>
                      <span className={`status ${product.prod_alive ? 'active' : 'inactive'}`}>
                        {product.prod_alive ? 'Activo' : 'Inactivo'}
                      </span>
                    </td>
                    <td>
                      <div className="action-buttons">
                        <button 
                          className={`toggle-status-btn ${product.prod_alive ? 'deactivate' : 'activate'}`}
                          onClick={() => handleToggleStatus(product.id, product.prod_alive)}
                          disabled={isUpdating}
                        >
                          {product.prod_alive ? 'Deshabilitar' : 'Habilitar'}
                        </button>
                        <button 
                          className="edit-btn"
                          onClick={() => handleEditClick(product)}
                        >
                          Editar
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>
      </div>
    </div>
  );
};

export default Products;
